<template>
  <div>
    <slot name="alert" />
    <McTable
      searchable
      :fields="selectDeliveryOrderFields"
      :apiClient="apiClient"
      :has-delete="false"
      :has-checkbox="true"
      :sticky-header="true"
      :with-card="false"
      :isFirstRowClickable="false"
      :initialLimit="5"
      :limitOptions="[5]"
      :initialSelectedIds="selectedOrderIds"
      @handleSelectValue="$emit('update:selectedOrderIds', $event)"
    >
      <template #cell(product_categories)="{ item }">
        <McChipList
          v-if="item?.product_categories && item?.product_categories?.length"
          :items="item.product_categories"
          :max-visible="3"
          :hideToggle="true"
          itemTextField="name"
        />
        <span v-else>-</span>
      </template>
    </McTable>
  </div>
</template>
<script>
import McTable from '@/components/McTable'
import McChipList from '@/components/McChipList'
import { mapGetters } from 'vuex'

import { formatIndexDate } from '@/helpers/tzDate'

import * as DeliveryOrder from '@/services/rest-api/lib/delivery/delivery-order'

export default {
  components: {
    McTable,
    McChipList,
  },
  props: {
    deliveryOrderIds: {
      type: Array,
      required: true,
    },
    selectedOrderIds: {
      type: Object,
      default: () => {},
    },
  },
  emits: ['update:selectedOrderIds'],
  data() {
    return {
      selectDeliveryOrderBaseFields: [
        {
          key: 'number',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.deliveryNumber'),
          isSortable: true,
        },
        {
          key: 'shipment_number',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.shipmentNumber'),
          isSortable: false,
          formatter: (value) => value || '-',
        },
        {
          key: 'reference_number',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.referenceNumber'),
          isSortable: false,
          formatter: (value) => value || '-',
        },
        {
          key: 'shipped_customer.name',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.customer'),
          isSortable: false,
        },
        {
          key: 'expected_pickup_on',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.targetPickedUp'),
          isSortable: false,
          formatter: (value) => {
            const datetime = value ? formatIndexDate(value) : '-'

            return datetime?.date && datetime?.time
              ? `${datetime.date}, ${datetime.time}`
              : datetime
          },
        },
        {
          key: 'expected_delivered_on',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.targetDelivered'),
          isSortable: false,
          formatter: (value) => {
            const datetime = value ? formatIndexDate(value) : '-'

            return datetime?.date && datetime?.time
              ? `${datetime.date}, ${datetime.time}`
              : datetime
          },
        },
        {
          key: 'timestamp.picked_on',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.actualPickedUp'),
          isSortable: false,
          formatter: (value) => {
            const datetime = value ? formatIndexDate(value) : '-'

            return datetime?.date && datetime?.time
              ? `${datetime.date}, ${datetime.time}`
              : datetime
          },
        },
        {
          key: 'load_min_temperature',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.minTemperature'),
          isSortable: false,
          formatter: (value) => ((value ?? '') === '' ? '-' : `${value} °C`),
        },
        {
          key: 'load_max_temperature',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.maxTemperature'),
          isSortable: false,
          formatter: (value) => ((value ?? '') === '' ? '-' : `${value} °C`),
        },
        {
          key: 'current_load_weight',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.weight'),
          isSortable: false,
          formatter: (value) => ((value ?? '') === '' ? '-' : `${value} Kg`),
        },
        {
          key: 'current_load_volume',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.volume'),
          isSortable: false,
          formatter: (value) => ((value ?? '') === '' ? '-' : `${value} Cbm`),
        },
        {
          key: 'product_categories',
          label: this.$t('fleetTask.transferDeliveryModal.deliveryOrderFields.productCategory'),
          isSortable: false,
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
    }),
    hasSingleProductCategoryAccess() {
      return this.hasAccessAll(['fleet-task-single-category-deliveries:read'])
    },
    selectDeliveryOrderFields() {
      if (this.hasSingleProductCategoryAccess) return this.selectDeliveryOrderBaseFields

      return this.selectDeliveryOrderBaseFields.filter((field) => field.key !== 'product_categories')
    },
    apiClient() {
      return {
        get: async (opts) => {
          const queryParamFilters = [`id in ${this.deliveryOrderIdsFormatted}`]

          const getDeliveryOrder = DeliveryOrder.get(queryParamFilters)
          const res = await getDeliveryOrder(opts)

          return res
        },
      }
    },
    deliveryOrderIdsFormatted() {
      return this.deliveryOrderIds.join(' ')
    },
  },
}
</script>
